import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { formatDate, formatCurrency } from '../../utils/dateUtils';
import { useDeleteInvoice } from '../../hooks/useInvoices';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { useCurrency } from '../../context/CurrencyContext';

const DeleteInvoiceModal = ({ isOpen, onClose, invoice }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const deleteInvoice = useDeleteInvoice();
    const { t } = useLanguage();
    const { currency } = useCurrency();

    const handleDelete = async () => {
        setDeleteError('');
        setIsDeleting(true);

        try {
            await deleteInvoice.mutateAsync({
                supplierUid: invoice.supplierUid,
                serviceUid: invoice.serviceUid,
                invoiceUid: invoice.uid,
            });
            onClose();
        } catch (error) {
            console.error('Error deleting invoice:', error);
            setDeleteError(error.message || t(KEYS.INVOICE_DELETE_ERROR));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setDeleteError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.INVOICE_DELETE_TITLE)}
            subtitle={t(KEYS.COMMON_CANNOT_UNDO)}
            size="small"
            footer={
                <>
                    <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? t(KEYS.COMMON_DELETING) : t(KEYS.COMMON_DELETE)}
                    </Button>
                </>
            }
        >
            {deleteError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {deleteError}
                </div>
            )}
            
            <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-status-overdue-bg rounded border border-status-overdue/20">
                    <AlertTriangle className="w-5 h-5 text-status-overdue flex-shrink-0 mt-0.5" />
                    <p className="text-text-primary font-medium">
                        Delete invoice <strong>{invoice?.name || invoice?.uid}</strong>?
                    </p>
                </div>
                
                {invoice && (
                    <div className="bg-surface-overlay rounded border border-surface-border p-4 space-y-2">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-muted">{t(KEYS.COMMON_AMOUNT)}</span>
                            <span className="text-sm font-medium text-text-primary">{formatCurrency(invoice.amount, currency)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-muted">{t(KEYS.COMMON_DATE_ISSUED)}</span>
                            <span className="text-sm font-mono text-text-primary">{formatDate(invoice.issue_date)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-muted">{t(KEYS.COMMON_DATE_DUE)}</span>
                            <span className="text-sm font-mono text-text-primary">{formatDate(invoice.due_date)}</span>
                        </div>
                    </div>
                )}
                
                <p className="text-sm text-text-muted">
                    This invoice will be permanently removed from the system.
                </p>
            </div>
        </Modal>
    );
};

export default DeleteInvoiceModal;
